import { useState } from 'react';
import type { PlanSlug } from '@/api/types';
import { formatPrice, type PlanPrices } from '@/billing/useSubscriptionPlans';
import { cx } from '@/lib/cx';
import { useT } from '@/i18n/LocaleProvider';
import styles from './Overlays.module.css';

interface Props {
  prices: PlanPrices;
  onCheckout: (plan: PlanSlug) => void;
  onDismiss: () => void;
}

/** Нижний paywall-док: появляется, когда пользователь доскроллил до блока лимита (README «Free-tier cap»). */
export function PaywallDock({ prices, onCheckout, onDismiss }: Props) {
  const [plan, setPlan] = useState<PlanSlug>('yearly');
  const t = useT();
  return (
    <div className={styles.dock} role="region" aria-label={t('catalog.dockTitle')}>
      <div className={styles.dockText}>
        <p className={styles.dockEyebrow}>{t('catalog.dockTitle')}</p>
        {/* Числа о размере каталога наружу не выходят (ADR-013). */}
        <p className={styles.dockTitle}>{t('catalog.dockMoreUnknown')}</p>
        <p className={styles.dockLead}>{t('catalog.dockLead')}</p>
      </div>
      <div className={styles.dockPlans} role="radiogroup" aria-label={t('billing.plan')}>
        <button
          type="button"
          role="radio"
          aria-checked={plan === 'monthly'}
          className={cx(styles.plan, plan === 'monthly' && styles.planSelected)}
          onClick={() => setPlan('monthly')}
        >
          <span className={styles.planName}>{t('billing.monthly')}</span>
          <span className={styles.planPrice}>
            {formatPrice(prices.monthly)}
            <span className={styles.planPeriod}> {t('billing.perMonth')}</span>
          </span>
        </button>
        <button
          type="button"
          role="radio"
          aria-checked={plan === 'yearly'}
          className={cx(styles.plan, plan === 'yearly' && styles.planSelected)}
          onClick={() => setPlan('yearly')}
        >
          {prices.yearlyDiscountPct ? (
            <span className={styles.planBadge}>−{prices.yearlyDiscountPct}%</span>
          ) : null}
          <span className={styles.planName}>{t('billing.yearly')}</span>
          <span className={styles.planPrice}>
            {formatPrice(prices.yearly)}
            <span className={styles.planPeriod}> {t('billing.perYear')}</span>
          </span>
        </button>
      </div>
      <div className={styles.dockActions}>
        <button type="button" className={styles.dockPrimary} onClick={() => onCheckout(plan)}>
          {t('catalog.continueToCheckout')}
        </button>
        <button type="button" className={styles.dockGhost} onClick={onDismiss}>
          {t('common.notNow')}
        </button>
      </div>
      <button
        type="button"
        className={styles.dockClose}
        title={t('common.notNow')}
        aria-label={t('common.dismiss')}
        onClick={onDismiss}
      >
        ×
      </button>
    </div>
  );
}
